import React, { useState } from 'react';
import { Eye, Pencil, Trash2, Shield, FileDown, FlaskConical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDate, formatCurrency, formatNumber } from '../../lib/utils';
import api from '../../lib/api';
import { toast } from 'sonner';
import { FormulaBreakdownDialog } from './FormulaBreakdownDialog';

// Colores para badges de animales
const ANIMAL_COLORS = {
  'pollo': 'bg-red-100 text-red-800',
  'gallina': 'bg-green-100 text-green-800',
  'cerdo': 'bg-pink-100 text-pink-800',
  'becerro': 'bg-amber-100 text-amber-800'
};

const ANIMAL_LABELS = {
  'pollo': 'Pollo',
  'gallina': 'Gallina',
  'cerdo': 'Cerdo',
  'becerro': 'Becerro'
};

export function DispatchesTable({
  filteredDispatches,
  medications,
  products,
  formulas,
  projects,
  isSuperUser,
  onView,
  onEdit,
  onDelete,
  calculateDispatchTotal
}) {
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownDispatchId, setBreakdownDispatchId] = useState(null);

  const handleViewBreakdown = async (dispatch) => {
    try {
      const response = await api.get(`/dispatches/${dispatch.id}/formula-breakdown`);
      setBreakdownData(response.data);
      setBreakdownDispatchId(dispatch.id);
    } catch (error) {
      toast.error('Error al cargar el desglose de la fórmula');
    }
  };
  
  const handleDownloadPDF = async (dispatch) => {
    try {
      const response = await api.get(`/dispatches/${dispatch.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `despacho_${dispatch.dispatch_number.replace(/-/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };
  
  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100">
        <CardTitle>Historial de Despachos</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>No. Despacho</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Proyecto</TableHead>
              <TableHead>Animal</TableHead>
              <TableHead className="text-center">Nave</TableHead>
              <TableHead className="text-center">Lote</TableHead>
              <TableHead className="text-right">Cantidad</TableHead>
              <TableHead className="text-right">Precio/qq</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredDispatches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                  No hay despachos registrados
                </TableCell>
              </TableRow>
            ) : (
              filteredDispatches.map((dispatch) => {
                const items = dispatch.items || [];
                const alimentoItems = items.filter(i => i.item_type === 'alimento');
                const medicamentoItems = items.filter(i => i.item_type === 'medicamento');
                const alimentoQty = alimentoItems.reduce((sum, i) => sum + (i.quantity || 0), 0);
                
                // Usar el precio guardado en el despacho (momento de creación)
                // Si no tiene precio guardado, usar el costo de la fórmula (igual que tabla de productos)
                let precioUnitarioUSD = 0;
                let precioUnitarioDOP = 0;
                
                // Verificar si hay medicamentos premix
                const hasPremix = medicamentoItems.some(med => 
                  med.is_premix || medications?.find(m => m.id === med.item_id)?.administration_type === 'premix'
                );
                
                if (alimentoQty > 0) {
                  if (hasPremix) {
                    // Si hay premix, calcular precio/qq = total / cantidad de qq
                    const totals = calculateDispatchTotal(dispatch);
                    precioUnitarioUSD = totals.usd / alimentoQty;
                    precioUnitarioDOP = totals.dop / alimentoQty;
                  } else if (alimentoItems.length > 0) {
                    const alimentoItem = alimentoItems[0];
                    
                    // Primero intentar usar el precio guardado en el item
                    if (alimentoItem.unit_price_usd) {
                      precioUnitarioUSD = alimentoItem.unit_price_usd;
                      precioUnitarioDOP = alimentoItem.unit_price_dop || (precioUnitarioUSD * 62);
                    } else {
                      // Fallback: usar el costo de la fórmula (igual que tabla de productos)
                      const product = products?.find(p => p.id === alimentoItem.item_id);
                      if (product && product.formula_id) {
                        const formula = formulas?.find(f => f.id === product.formula_id);
                        if (formula) {
                          precioUnitarioUSD = formula.unit_cost_usd || 0;
                          precioUnitarioDOP = formula.unit_cost_dop || (precioUnitarioUSD * 62);
                        }
                      }
                      // Si no hay fórmula, usar precio del producto
                      if (precioUnitarioUSD === 0 && product) {
                        precioUnitarioUSD = product.price_usd || 0;
                        precioUnitarioDOP = product.price_dop || (precioUnitarioUSD * 62);
                      }
                    }
                  }
                }
                
                return (
                  <TableRow key={dispatch.id} className={`hover:bg-slate-50 ${dispatch.is_return ? 'bg-amber-50/50' : ''}`}>
                    <TableCell className="font-mono font-medium text-[#931A25]">
                      <div className="flex items-center gap-1">
                        {dispatch.is_return && (
                          <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase" title="Devolución">
                            ↩ Dev
                          </span>
                        )}
                        {dispatch.dispatch_number}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(dispatch.dispatch_date)}</TableCell>
                    <TableCell className="font-medium">{dispatch.project_name}</TableCell>
                    <TableCell>
                      {(() => {
                        const project = projects?.find(p => p.id === dispatch.project_id);
                        const animalType = project?.animal_type;
                        if (animalType) {
                          return (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${ANIMAL_COLORS[animalType] || 'bg-blue-100 text-blue-800'}`}>
                              {ANIMAL_LABELS[animalType] || animalType}
                            </span>
                          );
                        }
                        return <span className="text-xs text-slate-400">-</span>;
                      })()}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                        Nave {dispatch.nave_number}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-medium">
                        Lote {dispatch.lote_number}
                      </span>
                    </TableCell>
                    <TableCell className="text-right min-w-[140px]">
                      <div>
                        {alimentoQty > 0 && (
                          <p className="font-semibold">{formatNumber(alimentoQty)} qq</p>
                        )}
                        {medicamentoItems.length > 0 && (
                          <div className={`${alimentoQty > 0 ? 'mt-1' : ''} space-y-0.5 text-left`}>
                            {medicamentoItems.map((med, idx) => {
                              const isPremix = med.is_premix || 
                                medications.find(m => m.id === med.item_id)?.administration_type === 'premix';
                              return (
                                <p key={idx} className="text-xs text-purple-600 whitespace-nowrap">
                                  {alimentoQty > 0 && <span className="text-purple-400">+ </span>}
                                  {formatNumber(med.quantity)} {med.unit || 'unidad'} <span className="font-medium">{med.item_name}</span>
                                  {isPremix && <span className="ml-1 bg-purple-100 text-purple-700 px-1 rounded text-[10px]">Premix</span>}
                                </p>
                              );
                            })}
                          </div>
                        )}
                        {alimentoQty === 0 && medicamentoItems.length === 0 && (
                          <p className="text-slate-400">-</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {precioUnitarioUSD > 0 ? (
                        <div>
                          <p className="font-semibold">{formatCurrency(precioUnitarioUSD, 'USD')}</p>
                          <p className="text-xs text-slate-500">({formatCurrency(precioUnitarioDOP)})</p>
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const totals = calculateDispatchTotal(dispatch);
                        return (
                          <div>
                            <p className={`font-semibold ${dispatch.is_return ? 'text-amber-600' : ''}`}>{formatCurrency(totals.usd, 'USD')}</p>
                            <p className={`text-sm ${dispatch.is_return ? 'text-amber-500' : 'text-blue-600'}`}>({formatCurrency(totals.dop)})</p>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onView(dispatch)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Ver detalle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewBreakdown(dispatch)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          title="Ver fórmula y costos"
                          data-testid={`view-formula-breakdown-${dispatch.dispatch_number}`}
                        >
                          <FlaskConical className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadPDF(dispatch)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Descargar PDF"
                          data-testid={`download-pdf-${dispatch.dispatch_number}`}
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onEdit(dispatch)}
                          className={`hover:bg-blue-50 ${isSuperUser ? 'text-blue-600 hover:text-blue-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={isSuperUser ? 'Editar' : 'Requiere Super Usuario'}
                          data-testid={`edit-dispatch-${dispatch.dispatch_number}`}
                        >
                          <Pencil className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onDelete(dispatch.id)}
                          className={`hover:bg-red-50 ${isSuperUser ? 'text-red-600 hover:text-red-700' : 'text-slate-400 hover:text-slate-500'}`}
                          title={isSuperUser ? 'Eliminar' : 'Requiere Super Usuario'}
                          data-testid={`delete-dispatch-${dispatch.dispatch_number}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          {!isSuperUser && <Shield className="h-3 w-3 ml-0.5 text-amber-500" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
      <FormulaBreakdownDialog
        open={!!breakdownData}
        onClose={() => { setBreakdownData(null); setBreakdownDispatchId(null); }}
        data={breakdownData}
        dispatchId={breakdownDispatchId}
      />
    </Card>
  );
}
